import { useEffect, useState } from 'react'
import { Loader2, AlertCircle, KeyRound } from 'lucide-react'
import { Modal, ModalHeader } from '@/components/Modal'
import { PLATFORM_LIST, type PlatformId } from '@/lib/platforms'
import { useStore } from '@/lib/store'
import {
  fetchProviders,
  connectBluesky,
  connectOAuth,
  type ProviderInfo,
} from '@/lib/connect'
import { reloadWorkspaceData } from '@/lib/data'
import { reconcileConnectedChannel } from '@/lib/reconcile-connection'
import { trackEvent } from '@/lib/posthog'
import { DisconnectChannelButton } from './DisconnectChannelButton'
import { ChannelAvatar } from '@/components/ChannelAvatar'
import clsx from 'clsx'

const CONNECTABLE: PlatformId[] = ['bluesky', 'mastodon', 'linkedin', 'x', 'threads', 'facebook', 'instagram']
const CONNECTABLE_PLATFORMS = PLATFORM_LIST.filter((p) => CONNECTABLE.includes(p.id))

export function ConnectChannelsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const channels = useStore((s) => s.channels)
  const [connecting, setConnecting] = useState<PlatformId | null>(null)
  const [providers, setProviders] = useState<Record<string, ProviderInfo>>({})
  const [providersReady, setProvidersReady] = useState(false)
  const [providersError, setProvidersError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<PlatformId | null>(null) // which inline form is open
  const [bsky, setBsky] = useState({ identifier: '', appPassword: '', service: '' })
  const [mastoHost, setMastoHost] = useState('')

  useEffect(() => {
    if (!open) return
    setProvidersReady(false)
    setProvidersError(null)
    fetchProviders().then((list) => {
      const map: Record<string, ProviderInfo> = {}
      for (const p of list) map[p.provider] = p
      setProviders(map)
      if (list.length === 0) {
        setProvidersError('The connection service is not reachable right now.')
      }
      setProvidersReady(true)
    })
  }, [open])

  const finishLive = async (platform: PlatformId, result: { ok: boolean; error?: string }) => {
    setForm(null)
    const shouldReconcile = result.ok || result.error === 'cancelled'
    const connectedAfterRefresh = shouldReconcile
      ? await reconcileConnectedChannel({
          platform,
          reload: reloadWorkspaceData,
          readChannels: () => useStore.getState().channels,
        })
      : false
    setConnecting(null)
    if (connectedAfterRefresh) {
      setError(null)
      trackEvent('account_connect_succeeded', { platform, method: 'live' })
    } else if (result.ok) {
      trackEvent('account_connect_failed', { platform, error: 'connection_not_visible' })
      setError(prettyError('connection_not_visible'))
    } else if (result.error && result.error !== 'cancelled') {
      trackEvent('account_connect_failed', { platform, error: result.error })
      setError(prettyError(result.error))
    }
  }

  const connect = async (platform: PlatformId) => {
    setError(null)
    const info = providers[platform]
    if (!info) {
      setError('The connection service is not reachable right now.')
      return
    }
    if (!info.configured) {
      setError(prettyError('not_configured'))
      return
    }
    // Providers needing a small input first.
    if (platform === 'bluesky') return setForm('bluesky')
    if (platform === 'mastodon') return setForm('mastodon')
    // OAuth popup.
    setConnecting(platform)
    finishLive(platform, await connectOAuth(platform))
  }

  const submitBluesky = async () => {
    if (!bsky.identifier || !bsky.appPassword) return
    setConnecting('bluesky')
    finishLive(
      'bluesky',
      await connectBluesky({
        identifier: bsky.identifier.trim(),
        appPassword: bsky.appPassword.trim(),
        service: bsky.service.trim() || undefined,
      }),
    )
  }

  const submitMastodon = async () => {
    if (!mastoHost.trim()) return
    setConnecting('mastodon')
    finishLive('mastodon', await connectOAuth('mastodon', { host: mastoHost.trim() }))
  }

  return (
    <Modal open={open} onClose={onClose} size="md" ariaLabel="Connect channels">
      <ModalHeader title="Connect channels" onClose={onClose} />
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="m-4 mb-0 flex items-start gap-2 rounded-xl border border-accent-red/40 bg-accent-red/10 p-3 text-sm text-accent-red">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {providersError && (
          <div className="m-4 mb-0 flex items-start gap-2 rounded-xl border border-accent-amber/40 bg-accent-amber/10 p-3 text-sm text-ink-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-accent-amber" />
            <span>{providersError}</span>
          </div>
        )}
        <div className="divide-y divide-line">
          {CONNECTABLE_PLATFORMS.map((p) => {
            const connectedChannel = channels.find((c) => c.platform === p.id)
            const isBusy = connecting === p.id
            const info = providers[p.id]
            const needsSetup = providersReady && info && !info.configured
            const unavailable = providersReady && (!info || needsSetup)
            return (
              <div key={p.id}>
                <div className="grid min-h-16 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-5 py-2.5">
                  <div className="flex min-w-0 items-center gap-3">
                    {connectedChannel ? (
                      <ChannelAvatar channel={connectedChannel} size="sm" />
                    ) : (
                      <span
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white"
                        style={{ background: p.gradient }}
                      >
                        <span className="scale-90">{p.icon}</span>
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block truncate font-display text-sm font-semibold text-ink-900">
                        {p.name}
                      </span>
                      {connectedChannel && (
                        <span className="block max-w-full truncate text-[11px] leading-4 text-ink-400">
                          {connectedChannel.handle}
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="flex min-w-0 justify-end">
                    {connectedChannel ? (
                      <div className="flex items-center justify-end gap-3">
                        <p className="flex items-center gap-1.5 text-xs font-medium text-accent-green">
                          <span className="h-1.5 w-1.5 rounded-full bg-accent-green" />
                          Connected
                        </p>
                        <DisconnectChannelButton channel={connectedChannel} compact />
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={isBusy || unavailable || !providersReady}
                        onClick={() => void connect(p.id)}
                        className={clsx(
                          'btn min-w-24 justify-center rounded-lg px-3 py-2 text-xs',
                          unavailable || !providersReady
                            ? 'cursor-not-allowed bg-panel text-ink-400 opacity-70'
                            : 'bg-brand-500 text-white hover:bg-brand-600',
                        )}
                      >
                        {isBusy ? (
                          <>
                            <Loader2 size={14} className="animate-spin" /> Connecting...
                          </>
                        ) : !providersReady ? (
                          'Checking...'
                        ) : unavailable ? (
                          'Unavailable'
                        ) : (
                          'Connect now'
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {form === p.id && p.id === 'bluesky' && (
                  <div className="border-t border-line bg-panel px-5 py-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <KeyRound size={15} className="text-brand-500" /> Connect Bluesky
                    </div>
                    <p className="mb-3 text-xs text-ink-500">
                      Use an <strong>app password</strong> from Bluesky Settings, App Passwords, not
                      your main password.
                    </p>
                    <div className="grid gap-2">
                      <input
                        className="input"
                        placeholder="handle.bsky.social"
                        value={bsky.identifier}
                        onChange={(e) => setBsky({ ...bsky, identifier: e.target.value })}
                      />
                      <input
                        className="input"
                        type="password"
                        placeholder="app password (xxxx-xxxx-xxxx-xxxx)"
                        value={bsky.appPassword}
                        onChange={(e) => setBsky({ ...bsky, appPassword: e.target.value })}
                      />
                      <div className="flex justify-end gap-2">
                        <button className="btn-ghost" onClick={() => setForm(null)}>
                          Cancel
                        </button>
                        <button
                          className="btn-primary"
                          onClick={submitBluesky}
                          disabled={connecting === 'bluesky'}
                        >
                          {connecting === 'bluesky' ? 'Connecting...' : 'Connect'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {form === p.id && p.id === 'mastodon' && (
                  <div className="border-t border-line bg-panel px-5 py-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <KeyRound size={15} className="text-brand-500" /> Connect Mastodon
                    </div>
                    <p className="mb-3 text-xs text-ink-500">
                      Enter your instance domain. We register an app there and open its login.
                    </p>
                    <div className="grid gap-2">
                      <input
                        className="input"
                        placeholder="mastodon.social"
                        value={mastoHost}
                        onChange={(e) => setMastoHost(e.target.value)}
                      />
                      <div className="flex justify-end gap-2">
                        <button className="btn-ghost" onClick={() => setForm(null)}>
                          Cancel
                        </button>
                        <button
                          className="btn-primary"
                          onClick={submitMastodon}
                          disabled={connecting === 'mastodon'}
                        >
                          {connecting === 'mastodon' ? 'Opening...' : 'Continue'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

function prettyError(code: string): string {
  switch (code) {
    case 'not_configured':
      return 'This network needs our developer app credentials, which aren’t set up yet.'
    case 'popup_blocked':
      return 'Your browser blocked the connect popup. Allow popups and try again.'
    case 'host_required':
      return 'Enter your Mastodon instance domain first.'
    case 'unauthorized':
      return 'Your session expired. Sign in again.'
    case 'connection_not_visible':
      return 'The network approved the connection, but it has not appeared here yet. Try connecting again.'
    default:
      return `Couldn’t connect: ${code}`
  }
}
